import { useCallback } from "react";
import type { MouseEvent } from "react";
import themeGet from "@styled-system/theme-get";
import {
  Box,
  Button,
  Divider,
  Flex,
  FlexItem,
  H5,
  Icon,
  Small,
} from "orcs-design-system";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";

import PATHS, { url } from "src/consts/urlPaths";
import { useUserTeamCanvasViews } from "src/hooks/userPreferences";
import { teamIdsSearch } from "src/components/TeamCanvas/useUrlTeamIds";
import { serializePresentationCollectionId } from "src/components/TeamCanvas/teamCanvasPresentation";

const ViewLink = styled(NavLink)`
  text-decoration: none !important;
  font-size: ${themeGet("fontSizes.1")};
  flex: 1 1 auto;
  min-width: 0;
  color: ${themeGet("colors.bodyText")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.active {
    color: ${themeGet("colors.primary")};
  }
`;

const ViewRow = styled(Flex)`
  padding: ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
  transition: ${themeGet("transition.transitionDefault")};
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet("space.xs")};

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
    ${ViewLink} {
      color: ${themeGet("colors.primary")};
    }
  }
`;

const TeamCanvasContent = () => {
  const { views, collections, deleteView, deleteCollection } =
    useUserTeamCanvasViews();

  const onDeleteView = useCallback(
    (event: MouseEvent, viewId: string) => {
      event.preventDefault();
      event.stopPropagation();
      deleteView(viewId);
    },
    [deleteView]
  );

  const onDeleteCollection = useCallback(
    (event: MouseEvent, collectionId: string) => {
      event.preventDefault();
      event.stopPropagation();
      deleteCollection(collectionId);
    },
    [deleteCollection]
  );

  return (
    <Flex flexDirection="column" height="100%">
      <FlexItem>
        <Box mb="r">
          <Flex alignItems="center" gap="xs">
            <Icon icon={["fas", "sitemap"]} color="primary" />
            <H5 fontWeight="bold" data-testid="heading">
              Team Canvas
            </H5>
          </Flex>

          <Box my="r">
            <Divider light />
          </Box>

          <ViewLink exact to={url(PATHS.TEAM_CANVAS)}>
            <span>Open Team Canvas</span>
            <Icon icon={["fas", "plus"]} />
          </ViewLink>

          <Box my="r">
            <Small color="greyDark" fontWeight="bold">
              Saved views
            </Small>
          </Box>

          {views.length === 0 && (
            <Small color="greyMedium">
              No saved views yet. Add teams to the canvas and use "Save view" to
              keep the selection here.
            </Small>
          )}

          <Flex flexDirection="column" gap="xs">
            {views.map((view) => (
              <ViewRow
                key={view.id}
                data-testid={`team-canvas-saved-view-${view.id}`}
              >
                <ViewLink
                  to={`${url(PATHS.TEAM_CANVAS)}${teamIdsSearch(view.teamIds)}`}
                >
                  {view.name}
                </ViewLink>
                <Flex alignItems="center" gap="xs" flex="0 0 auto">
                  <Small fontSize="0.75" color="mutedText">
                    {moment(view.createdAt).fromNow()}
                  </Small>
                  <Button
                    type="button"
                    variant="ghost"
                    small
                    iconOnly
                    ariaLabel={`Delete saved view ${view.name}`}
                    onClick={(event: MouseEvent) =>
                      onDeleteView(event, view.id)
                    }
                    data-testid={`team-canvas-delete-view-${view.id}`}
                  >
                    <Icon icon={["far", "trash-alt"]} />
                  </Button>
                </Flex>
              </ViewRow>
            ))}
          </Flex>

          <Box my="r">
            <Small color="greyDark" fontWeight="bold">
              Collections
            </Small>
          </Box>

          {collections.length === 0 && (
            <Small color="greyMedium">
              No collections yet. Use the Team Canvas header to create an
              ordered presentation from saved views.
            </Small>
          )}

          <Flex flexDirection="column" gap="xs">
            {collections.map((collection) => (
              <ViewRow
                key={collection.id}
                data-testid={`team-canvas-collection-${collection.id}`}
              >
                <ViewLink
                  to={`${url(PATHS.TEAM_CANVAS)}${serializePresentationCollectionId("", collection.id)}`}
                >
                  {collection.name}
                </ViewLink>
                <Flex alignItems="center" gap="xs" flex="0 0 auto">
                  <Small fontSize="0.75" color="mutedText">
                    {collection.viewIds.length} slide
                    {collection.viewIds.length === 1 ? "" : "s"}
                  </Small>
                  <Button
                    type="button"
                    variant="ghost"
                    small
                    iconOnly
                    ariaLabel={`Delete collection ${collection.name}`}
                    onClick={(event: MouseEvent) =>
                      onDeleteCollection(event, collection.id)
                    }
                    data-testid={`team-canvas-delete-collection-${collection.id}`}
                  >
                    <Icon icon={["far", "trash-alt"]} />
                  </Button>
                </Flex>
              </ViewRow>
            ))}
          </Flex>
        </Box>
      </FlexItem>
    </Flex>
  );
};

export default TeamCanvasContent;
